function getURL(url: string | URL): URL {
  if (!url) {
    throw new Error('Provided url is not valid');
  }

  if (url instanceof URL) {
    return url;
  }

  try {
    return new URL(url);
  } catch {
    throw new Error('Provided url cannot be converted to a real URL');
  }
}

export type ApiCallOptions = {
  headers?: Record<string, string>;
  options?: Exclude<Partial<RequestInit>, 'headers'>;
  timeout?: number;
  token?: string;
  warn?: boolean;
};

/**
 * Make a generic call to an API endpoint with the ability to abort after a timeout.
 * @param url The URL to run the API call against. Needs to be a valid full URL, but
 *        can be provided as a URL object or a string.
 * @param config - used to configure the API call.
 *        - config.headers: additional headers to set for the fetch call.
 *        - config.options: options used to configure the fetch call. See fetch init object. Do not
 *        set config.options.headers!
 *        - config.timeout = 750: timeout in ms to wait for the fetch call to finish.
 *        - config.token: OAuth2 bearer token to use to authenticate the call.
 *        - config.warn = true: flag weather to warn  when aborting the request due to timeout.
 * @returns This method should always return a valid
 * object for use. The `ok` flag indicates whether the call succeeded or not and if you can access
 * the data property. If the call fails due to an exception, the error property contains the caught
 * error. If the call fails due to an HTTP error, the response object in its entirety is provided
 * for further introspection.
 */
export async function apiCall<T = unknown>(
  url: string | URL,
  config: ApiCallOptions = {},
): Promise<
  | { ok: true; data: T }
  | { ok: false; response: Response; timeout: boolean }
  | { ok: false; error: unknown; timeout: boolean }
> {
  const callUrl = getURL(url);

  const callConfig: ApiCallOptions = {
    warn: true,
    ...(config || {}),
  };

  if (callConfig?.options?.headers) {
    throw new Error('Use config.headers instead of config.options.headers');
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json; charset=utf-8',
    ...(callConfig.headers || {}),
  };

  if (callConfig.token) {
    if (callConfig.headers?.Authorization) {
      throw new Error('Do not specify both config.token and config.headers.Authorization');
    }

    headers.Authorization = `Bearer ${callConfig.token}`;
  }

  const options: RequestInit = {
    cache: 'no-store',
    headers,
    method: 'GET',
    mode: 'cors',
    ...(callConfig.options || {}),
  };

  const controller = new AbortController();
  options.signal = controller.signal;

  const delay = callConfig.timeout || 750;
  const warning = callConfig.warn && new Error(`apiCall timeout after ${delay}ms.`);

  let timeout: ReturnType<typeof setTimeout> | null = setTimeout(() => {
    timeout = null;
    controller.abort();

    if (callConfig.warn) {
      console.warn(warning);
    }
  }, delay);

  let response;
  try {
    response = await fetch(callUrl.toString(), options);
  } catch (error) {
    return {
      ok: false,
      error,
      timeout: error instanceof Error && error.name === 'AbortError',
    };
  } finally {
    clearTimeout(timeout);
  }

  if (!response.ok) {
    return {
      ok: false,
      response,
      timeout: false,
    };
  }

  return {
    ok: true,
    data: await response.json(),
  };
}
